/**
 * Copyright (c) Omnissa, LLC. All rights reserved.
 * This product is protected by copyright and intellectual property laws in the
 * United States and other countries as well as by international treaties.
 * -- Omnissa Restricted
 *
 * @format
 */

/**
 * Used at the channel part mainly for RX feature to get and send data,
 * current only used with seamless window beta, to reduce the refactor effort.
 */

import { Port } from "../../../chrome-client/base/model/rx-bridge-type";
import { Injectable } from "@angular/core";
import {
   SessionMsg,
   RemoteSessionEventService
} from "../../../shared/common/remote-session/remote-session-event.service";
import { RTAVService } from "../../../shared/desktop/rtav/rtav.service";
import { FeatureLogger } from "../../../shared/desktop/common/feature-logger.service";
import { ConnectedMessageService } from "../../../native-shared/base/connected-message.service";
import { IwaShareFolderModel } from "../../common/model/iwa-shared-folder-model";
import { CDRService } from "../../../shared/desktop/cdr/cdr-service";
import { MultimonService } from "../../../shared/desktop/multimon/main-page/multimon.service";
import { AudioPlayControl } from "../../../shared/desktop/common/audio-play.control";
import { AB } from "../../../shared/desktop/common/appblast-util.service";
import { FileAssociationService } from "../../../native-shared/desktop/remote-desktop/file-association.service";
import { UrlParameterService } from "../../../chrome-client/desktop/blast-common/url-parameter.service";
import { RTAV_H264_CODEC, RTAV_H264_OPUS_CODEC } from "../../../shared/desktop/rtav/rtav.constants";
import { Logger, clientUtil } from "../../../core/libs";
import { RxBridgeBaseChannel } from "../../../native-shared/desktop/rx-bridge-base.service";
import { CDRConfig } from "../../../shared/desktop/cdr/cdr-config";

@Injectable()
export class IWARxBridgeChannel extends RxBridgeBaseChannel {
   private _channelEnableStatus;
   private _sessionKey;
   private _vvcSession;
   private _vdpService;
   private _rxLogger: any = FeatureLogger("RX")(Logger);
   private _rtavInitArgs = {
      enableRTAVH264Codec: true,
      enableRTAVOpusCodec: true,
      enableRTAVDTX: true,
      hardwareAccelerationOption: "no-preference"
   };

   constructor(
      private _connectedMessageService: ConnectedMessageService,
      private _remoteSessionEventService: RemoteSessionEventService,
      private _cdrService: CDRService,
      private _multimonService: MultimonService,
      private _fileAssociationService: FileAssociationService,
      private _urlParameterService: UrlParameterService,
      private _rtavService: RTAVService,
      private _iwaShareFolderModel: IwaShareFolderModel,
      private _cdrConfig: CDRConfig
   ) {
      super();
   }

   public init = (sessionKey, channelEnableStatus, rxParams) => {
      this._rxLogger.info("RxBridgeChannel inited");
      this._channelEnableStatus = channelEnableStatus;
      this._sessionKey = sessionKey;
      // TODO: check settings to decide whether init coresponding ports here
      this._connectedMessageService.initConnection(sessionKey, Port.ChannelName.Session);
      this._remoteSessionEventService.addEventListener(SessionMsg.SESSION_CONNECTING_MSG, this.onSessionConnecting);
      this._remoteSessionEventService.addEventListener(SessionMsg.SESSION_CONNECTED_MSG, this.onSessionConnected);
      this._remoteSessionEventService.addEventListener(SessionMsg.SESSION_DISCONNECTED_MSG, this.onSessionDisconnected);
      this._remoteSessionEventService.addEventListener(SessionMsg.SESSION_REMOVED_MSG, this.onSessionClosed);

      if (channelEnableStatus.RTAV) {
         this._rxLogger.info("init RTAV by connection");
         this._connectedMessageService.initConnection(sessionKey, Port.ChannelName.RTAV);
      } else {
         this._initRTAVLocally();
      }
      if (channelEnableStatus.CDR) {
         this._connectedMessageService.initConnection(sessionKey, Port.ChannelName.CDR);
      }
      this._connectedMessageService.initConnection(sessionKey, Port.ChannelName.USB);
   };

   private _initRTAVLocally = () => {
      this._rxLogger.info("init RTAV locally");
      this._getRtavInitArgsFromPolicy();
      this._rtavService.init(this._rtavInitArgs);
      const ABInstance = new AB(this._rtavService);
      this._rtavService.onDeviceStatusChanged(() => {
         const deviceStatus = AB.getRTAVDeviceStatus();
         if (deviceStatus?.deviceInUsed?.audio) {
            AudioPlayControl.setRTAVActive(true, this._sessionKey);
         } else {
            AudioPlayControl.setRTAVActive(false, this._sessionKey);
         }
      });
   };

   private _getRtavInitArgsFromPolicy = () => {
      this._rxLogger.debug(
         "[RTAV]: urlparameterService has set enableRTAVH264Codec kill switch to: " +
            this._urlParameterService.launchSettings.enableRTAVH264Codec +
            " from policy for chrome client"
      );
      this._rxLogger.debug(
         "[RTAV]: urlparameterService has set enableRTAVOpusCodec kill switch to: " +
            this._urlParameterService.launchSettings.enableRTAVOpusCodec +
            " from policy for chrome client"
      );
      this._rxLogger.debug(
         "[RTAV]: urlparameterService has set enableRTAVDTX kill switch to: " +
            this._urlParameterService.launchSettings.enableRTAVDTX +
            " from policy for chrome client"
      );
      this._rxLogger.debug(
         "[RTAV]: urlparameterService has set hardwareAccelerationOption kill switch to: " +
            this._urlParameterService.launchSettings.hardwareAccelerationOption +
            " from policy for chrome client"
      );
      this._rtavInitArgs = {
         enableRTAVH264Codec: this._urlParameterService.launchSettings.enableRTAVH264Codec,
         enableRTAVOpusCodec: this._urlParameterService.launchSettings.enableRTAVOpusCodec,
         enableRTAVDTX: this._urlParameterService.launchSettings.enableRTAVDTX,
         hardwareAccelerationOption: this._urlParameterService.launchSettings.hardwareAccelerationOption
      };
   };

   private _listenRTAVDeviceStatusChange = () => {
      this._connectedMessageService.onMessage(this._sessionKey, Port.ChannelName.RTAV, (msg) => {
         this._rxLogger.trace("rtav response get" + JSON.stringify(msg.content));
         // only use audio active for delay control
         if (msg.type === Port.RTAVMsg.DeviceStatus) {
            if (msg.content && msg.content.audio) {
               AudioPlayControl.setRTAVActive(true, this._sessionKey);
            } else {
               AudioPlayControl.setRTAVActive(false, this._sessionKey);
            }
         }
      });
   };

   private _createRTAVChannel = (vvcSession) => {
      if (!vvcSession) {
         return;
      }
      if (!this._channelEnableStatus.RTAV) {
         this._rxLogger.info("RTAV is disabled for this blast session" + this._sessionKey);
         return;
      }
      this._rxLogger.info("preparing RTAV channel for this blast session" + this._sessionKey);
      let initialDataArr = null;
      this._rxLogger.debug(
         "[RTAV]: test vvcSession.enableRTAVH264Codec in rx-bridge-channel" + vvcSession.enableRTAVH264Codec
      );
      this._rxLogger.debug(
         "[RTAV]: test vvcSession.enableRTAVOpusCodec in rx-bridge-channel" + vvcSession.enableRTAVOpusCodec
      );
      if (vvcSession.enableRTAVH264Codec !== false && vvcSession.enableRTAVOpusCodec !== false) {
         initialDataArr = clientUtil.pushBytes(RTAV_H264_OPUS_CODEC, 4, false);
      } else if (vvcSession.enableRTAVH264Codec !== false && vvcSession.enableRTAVOpusCodec === false) {
         initialDataArr = clientUtil.pushBytes(RTAV_H264_CODEC, 4, false);
      }
      let rtavChannel = vvcSession.openChannel("MultimediaRedirChannel", 10, null, null, initialDataArr);
      rtavChannel.onopen = () => {
         this._rxLogger.debug("rtav channel is opened");
         this._connectedMessageService.sendMessage(this._sessionKey, Port.ChannelName.RTAV, {
            type: Port.RTAVMsg.ChannelOpen,
            wmksKey: this._sessionKey
         });
      };
      rtavChannel.onclose = () => {
         this._rxLogger.debug("rtav channel is closed");
         this._connectedMessageService.sendMessage(this._sessionKey, Port.ChannelName.RTAV, {
            type: Port.RTAVMsg.ChannelClose,
            wmksKey: this._sessionKey
         });
      };
      rtavChannel.onerror = () => {
         this._rxLogger.debug("rtav channel is error");
         this._connectedMessageService.sendMessage(this._sessionKey, Port.ChannelName.RTAV, {
            type: Port.RTAVMsg.ChannelError,
            wmksKey: this._sessionKey
         });
      };
      rtavChannel.onmessage = (message) => {
         this._rxLogger.trace("rtav channel get message" + JSON.stringify(message));
         this._connectedMessageService.sendMessage(this._sessionKey, Port.ChannelName.RTAV, {
            type: Port.RTAVMsg.ChannelMessage,
            wmksKey: this._sessionKey,
            content: {
               type: message.type,
               timeStamp: message.timeStamp,
               data: Array.from(new Uint8Array(message.data))
            }
         });
      };
      this._connectedMessageService.onMessage(this._sessionKey, Port.ChannelName.RTAV, (msg) => {
         this._rxLogger.trace("rtav response get" + JSON.stringify(msg.content));
         if (msg.type === Port.RTAVMsg.ChannelResponse) {
            const networkBytes = new Uint8Array(msg.content);
            vvcSession.send(rtavChannel, networkBytes);
         }
      });
   };

   private _cdrOnMessageHandler = async (msg) => {
      this._rxLogger.trace("[Windows/App] cdr response get" + JSON.stringify(msg));
      if (msg.type === Port.CDRMsg.cdrReady) {
         this._connectedMessageService.sendMessage(this._sessionKey, Port.ChannelName.CDR, {
            type: Port.CDRMsg.getLatestFileInfo,
            wmksKey: this._sessionKey
         });
      } else if (msg.type === Port.CDRMsg.newFolderFromFA) {
         const IwaFileInfo = msg.content;
         this._fileAssociationService.updateFileInfo(IwaFileInfo);
         // Accounts for the case where CDR is disabled. If user launches regular app first,
         // fromFileAssociation will not be set and tsdr channel will not be initialized so
         // need to initialize on FA launch.
         if (!this._cdrConfig.isEnabled() && this._cdrService.isTsdrChannelDisconnected()) {
            clientUtil.updateFAStatus(true);
            this._cdrService.onConnecting(this._sessionKey, this._vdpService);
         }
      }
   };

   private _initCDR = async () => {
      if (!this._channelEnableStatus.CDR) {
         this._rxLogger.info("CDR is disabled for this blast session " + this._sessionKey);
         return;
      }
      this._rxLogger.info("preparing CDR channel for this blast session " + this._sessionKey);
      this._connectedMessageService.onMessage(this._sessionKey, Port.ChannelName.CDR, this._cdrOnMessageHandler);
      //get the folder entry from indexedDB and update it in shared folder
      await this._iwaShareFolderModel.getDisplayInfo();
   };

   public onSessionConnecting = async (wmksSession) => {
      this._rxLogger.debug("inform parent window that blast session connecting");
      this._rxLogger.debug("RTAV ready to be connected");
      this._connectedMessageService.sendMessage(this._sessionKey, Port.ChannelName.Session, {
         type: Port.SessionMsg.onConnecting
      });
      this._getRtavInitArgsFromPolicy();
      if (!this._channelEnableStatus.RTAV) {
         this._rtavService.onConnecting(wmksSession.key, wmksSession.vdpService.vvcSession, this._rtavInitArgs);
      }
      this._vvcSession = wmksSession.vdpService.vvcSession;
      this._vdpService = wmksSession.vdpService;
      await this._initCDR();
      //Will create tsdr channel for CDR
      this._cdrService.onConnecting(this._sessionKey, this._vdpService);
   };

   public onSessionConnected = (session) => {
      this._rxLogger.debug("inform parent window that blast session connected");
      if (!this._channelEnableStatus.RTAV) {
         this._rtavService.onConnected(session.key);
      } else {
         this._createRTAVChannel(this._vvcSession);
         this._listenRTAVDeviceStatusChange();
      }
   };

   public onSessionDisconnecting = (key) => {
      if (!this._channelEnableStatus.RTAV) {
         this._rtavService.onDisconnected(key);
      }
   };

   public onSessionDisconnected = (session) => {
      this._rxLogger.debug("inform parent window that blast session disconnected");
      this._multimonService.destroyMultimon();
      if (!this._channelEnableStatus.RTAV) {
         this._rtavService.onDisconnected(session.entitleId);
      }
   };

   public onSessionClosed = () => {
      this._rxLogger.debug("inform parent window that blast session has ended and window should be closed");
      this._multimonService.destroyMultimon();
   };
}
